import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/api/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Users, MagnifyingGlass, GridFour, ListBullets, PencilSimple, MapPin, Phone, EnvelopeSimple, Cake, CurrencyDollar, ShoppingCart, Trophy, ClockCounterClockwise, Briefcase, IdentificationCard, House, FloppyDisk, X, FileText, FilePdf, UserCircle, ChatCircleDots, WhatsappLogo, MessengerLogo, CheckCircle, ChartLine, Calendar } from '@phosphor-icons/react';

const STATUS_CONFIG = {
  active: { label: 'Activo', color: 'emerald', dot: 'bg-emerald-400' },
  vacation: { label: 'Vacaciones', color: 'blue', dot: 'bg-blue-400' },
  suspended: { label: 'Suspendido', color: 'amber', dot: 'bg-amber-400' },
  inactive: { label: 'Inactivo', color: 'slate', dot: 'bg-slate-500' },
};

const POSITIONS = ['Vendedor Junior', 'Vendedor Senior', 'Líder de Zona', 'Supervisor', 'Otro'];

const ROLES = {
  user: {
    label: 'Vendedor',
    icon: '👤',
    color: 'slate',
    description: 'Acceso al CRM como agente de ventas.',
    permissions: [
      'Ver y responder SOLO sus conversaciones asignadas',
      'Crear pedidos para sus clientes',
      'Ver su propio ranking y métricas personales',
      'Editar su perfil (foto, password)',
      'NO puede asignar conversaciones a otros',
      'NO ve datos de otros vendedores',
    ],
  },
  supervisor: {
    label: 'Supervisor',
    icon: '🎯',
    color: 'blue',
    description: 'Líder de equipo. Puede gestionar vendedores y conversaciones de su área.',
    permissions: [
      'TODO lo que puede un vendedor',
      'Ver TODAS las conversaciones del negocio',
      'Asignar/reasignar conversaciones a vendedores',
      'Ver y editar ficha de vendedores (departamentos, posición, etc.)',
      'Ver ranking completo del equipo',
      'Generar reportes PDF de vendedores',
      'NO puede editar credenciales Meta ni crear negocios',
      'NO puede cambiar roles ni reactivar usuarios',
    ],
  },
  admin: {
    label: 'Administrador',
    icon: '👑',
    color: 'amber',
    description: '⚠️ Control TOTAL del CRM. Solo dale este rol a personas de absoluta confianza.',
    permissions: [
      'TODO lo que puede un supervisor',
      'Crear, editar y eliminar negocios',
      'Configurar credenciales Meta (Messenger, WhatsApp)',
      'Crear, editar, activar/desactivar usuarios',
      'Asignar roles (incluso promover a otros admins)',
      'Configurar Modo Vendedor Único por negocio',
      'Editar nombres y configuración de Bot IA',
      'Ver audit log completo y exportar datos',
      'Acceso a todos los negocios y módulos',
    ],
  },
};


const GoalsTab = ({ vendor, isAdmin: isAdminProp = false }) => {
  const [form, setForm] = useState({
    daily_calls: '', daily_visits: '', daily_quotes: '',
    monthly_calls: '', monthly_visits: '', monthly_quotes: '',
    target_cyh: '', target_sa: '',
  });
  const [weekly, setWeekly] = useState({ weeks: [], totals: { sales_cyh: 0, sales_sa: 0, sales_total: 0 }, can_edit: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exists, setExists] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() + 1 });

  const year = selectedPeriod.year;
  const month = selectedPeriod.month;
  const targetTotal = (parseFloat(form.target_cyh) || 0) + (parseFloat(form.target_sa) || 0);

  const reloadAll = async () => {
    setLoading(true);
    try {
      const [gRes, wRes] = await Promise.all([
        api.get(`/work-plan/goals?user_id=${vendor.id}&year=${year}&month=${month}`),
        api.get(`/work-plan/weekly-sales?user_id=${vendor.id}&year=${year}&month=${month}`),
      ]);
      const g = Array.isArray(gRes.data) ? (gRes.data[0] || null) : gRes.data;
      if (g) {
        setExists(true);
        setForm({
          daily_calls: g.daily_calls ?? '',
          daily_visits: g.daily_visits ?? '',
          daily_quotes: g.daily_quotes ?? '',
          monthly_calls: g.monthly_calls ?? '',
          monthly_visits: g.monthly_visits ?? '',
          monthly_quotes: g.monthly_quotes ?? '',
          target_cyh: g.target_cyh ?? '',
          target_sa: g.target_sa ?? '',
        });
      } else {
        setExists(false);
        setForm({ daily_calls: '', daily_visits: '', daily_quotes: '',
          monthly_calls: '', monthly_visits: '', monthly_quotes: '',
          target_cyh: '', target_sa: '' });
      }
      setWeekly(wRes.data);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor.id, year, month]);

  const num = (v) => v === '' || v === null ? null : Number(v);

  const handleSaveGoal = async () => {
    const cyh = parseFloat(form.target_cyh) || 0;
    const sa = parseFloat(form.target_sa) || 0;
    if (cyh + sa <= 0) {
      toast.error('Asigna al menos una meta (CYH o SA)');
      return;
    }
    setSaving(true);
    try {
      await api.put('/work-plan/goals', {
        user_id: vendor.id,
        year, month,
        target_amount: cyh + sa,
        target_cyh: cyh, target_sa: sa,
        daily_calls: num(form.daily_calls),
        daily_visits: num(form.daily_visits),
        daily_quotes: num(form.daily_quotes),
        monthly_calls: num(form.monthly_calls),
        monthly_visits: num(form.monthly_visits),
        monthly_quotes: num(form.monthly_quotes),
      });
      toast.success('Plan de trabajo guardado');
      setExists(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando metas');
    } finally {
      setSaving(false);
    }
  };

  const updateWeek = async (weekNum, field, value) => {
    const v = parseFloat(value) || 0;
    const current = weekly.weeks.find(w => w.week === weekNum) || { sales_cyh: 0, sales_sa: 0 };
    const next = { ...current, [field]: v };
    try {
      await api.put('/work-plan/weekly-sales', {
        user_id: vendor.id, year, month, week: weekNum,
        sales_cyh: field === 'sales_cyh' ? v : current.sales_cyh,
        sales_sa: field === 'sales_sa' ? v : current.sales_sa,
      });
      // Refresh
      const wRes = await api.get(`/work-plan/weekly-sales?user_id=${vendor.id}&year=${year}&month=${month}`);
      setWeekly(wRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando venta semanal');
    }
  };

  const monthLabel = new Date(year, month - 1).toLocaleDateString('es-GT', { month: 'long', year: 'numeric' });
  const pctCyh = parseFloat(form.target_cyh) ? Math.round((weekly.totals.sales_cyh / parseFloat(form.target_cyh)) * 100 * 10) / 10 : null;
  const pctSa = parseFloat(form.target_sa) ? Math.round((weekly.totals.sales_sa / parseFloat(form.target_sa)) * 100 * 10) / 10 : null;
  const pctTotal = targetTotal ? Math.round((weekly.totals.sales_total / targetTotal) * 100 * 10) / 10 : null;

  if (loading) {
    return <p className="text-xs text-[var(--text-muted)] text-center py-6">Cargando plan...</p>;
  }

  return (
    <div className="space-y-4" data-testid="goals-tab">
      {/* Period selector */}
      <div className="flex items-center justify-between gap-2 bg-[var(--surface)] border border-[var(--border)] rounded-md p-2.5">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-[var(--text-faint)] font-bold">Periodo</p>
          <p className="text-sm font-black text-[var(--text)] capitalize">{monthLabel}</p>
        </div>
        <div className="flex gap-1">
          <select value={month} onChange={(e) => setSelectedPeriod({ ...selectedPeriod, month: Number(e.target.value) })}
            className="bg-[var(--bg)] border border-[var(--border)] text-[var(--text)] rounded px-2 py-1 text-xs"
            data-testid="goal-month-select">
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m =>
              <option key={m} value={m}>{new Date(2000, m - 1).toLocaleDateString('es-GT', { month: 'long' })}</option>
            )}
          </select>
          <select value={year} onChange={(e) => setSelectedPeriod({ ...selectedPeriod, year: Number(e.target.value) })}
            className="bg-[var(--bg)] border border-[var(--border)] text-[var(--text)] rounded px-2 py-1 text-xs"
            data-testid="goal-year-select">
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* Targets section — Admin only (supervisors solo cargan ventas, no metas) */}
      {isAdminProp && (
      <div>
        <p className="text-[10px] uppercase tracking-wide font-black text-[var(--text-muted)] mb-2 flex items-center gap-1.5">
          <Trophy size={12} weight="bold" /> Metas mensuales de ventas (Q)
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Field label="CYH (Complementos)">
            <Input type="number" min="0" value={form.target_cyh}
              onChange={(e) => setForm({ ...form, target_cyh: e.target.value })}
              placeholder="0" className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] font-black"
              data-testid="goal-target-cyh" />
          </Field>
          <Field label="SA (Soluciones Arq.)">
            <Input type="number" min="0" value={form.target_sa}
              onChange={(e) => setForm({ ...form, target_sa: e.target.value })}
              placeholder="0" className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] font-black"
              data-testid="goal-target-sa" />
          </Field>
          <Field label="Total (auto)">
            <Input value={`Q ${targetTotal.toLocaleString('es-GT')}`} disabled
              className="bg-[var(--surface-muted)] border-[var(--border)] text-[var(--text)] font-black opacity-90"
              data-testid="goal-target-total" />
          </Field>
        </div>
      </div>
      )}

      {/* Read-only meta summary for supervisors (so they know what they're tracking against) */}
      {!isAdminProp && (form.target_cyh || form.target_sa) && (
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-md p-3">
        <p className="text-[10px] uppercase tracking-wide font-black text-[var(--text-muted)] mb-2 flex items-center gap-1.5">
          <Trophy size={12} weight="bold" /> Metas del mes
        </p>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div><span className="text-[var(--text-muted)]">CYH:</span> <span className="font-black text-[var(--text)]">Q {Number(form.target_cyh || 0).toLocaleString('es-GT')}</span></div>
          <div><span className="text-[var(--text-muted)]">SA:</span> <span className="font-black text-[var(--text)]">Q {Number(form.target_sa || 0).toLocaleString('es-GT')}</span></div>
          <div><span className="text-[var(--text-muted)]">Total:</span> <span className="font-black text-emerald-600 dark:text-emerald-400">Q {targetTotal.toLocaleString('es-GT')}</span></div>
        </div>
      </div>
      )}

      {/* Weekly sales grid */}
      <div>
        <p className="text-[11px] uppercase tracking-wide font-black text-[var(--text-muted)] mb-2 flex items-center gap-1.5">
          <Calendar size={14} weight="bold" /> Ventas reales por semana
          {!weekly.can_edit && <span className="text-[9px] text-[var(--text-faint)] normal-case font-normal">(solo lectura — sin permiso)</span>}
        </p>

        {/* Desktop: table */}
        <div className="hidden sm:block bg-[var(--surface)] border border-[var(--border)] rounded-md overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[var(--surface-muted)] text-[10px] uppercase font-bold tracking-wide text-[var(--text-faint)]">
                <th className="px-2 py-1.5 text-left">Sem</th>
                <th className="px-2 py-1.5 text-right">CYH (Q)</th>
                <th className="px-2 py-1.5 text-right">SA (Q)</th>
                <th className="px-2 py-1.5 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {weekly.weeks.map((w) => (
                <tr key={w.week} className="border-t border-[var(--border)]" data-testid={`week-row-${w.week}`}>
                  <td className="px-2 py-1 font-black text-[var(--text)]">{w.week}</td>
                  <td className="px-2 py-1">
                    <input type="number" min="0" defaultValue={w.sales_cyh || ''} disabled={!weekly.can_edit}
                      onBlur={(e) => updateWeek(w.week, 'sales_cyh', e.target.value)}
                      className="w-full text-right bg-transparent text-[var(--text)] border border-transparent hover:border-[var(--border)] focus:border-[var(--primary)] rounded px-1.5 py-0.5 disabled:opacity-60"
                      data-testid={`week-${w.week}-cyh`} placeholder="0" />
                  </td>
                  <td className="px-2 py-1">
                    <input type="number" min="0" defaultValue={w.sales_sa || ''} disabled={!weekly.can_edit}
                      onBlur={(e) => updateWeek(w.week, 'sales_sa', e.target.value)}
                      className="w-full text-right bg-transparent text-[var(--text)] border border-transparent hover:border-[var(--border)] focus:border-[var(--primary)] rounded px-1.5 py-0.5 disabled:opacity-60"
                      data-testid={`week-${w.week}-sa`} placeholder="0" />
                  </td>
                  <td className="px-2 py-1 text-right font-black text-[var(--text)]">
                    Q {w.sales_total.toLocaleString('es-GT')}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-[var(--primary)] bg-[var(--surface-muted)]">
                <td className="px-2 py-1.5 font-black text-[var(--text)] uppercase">Total</td>
                <td className="px-2 py-1.5 text-right font-black text-[var(--text)]">Q {weekly.totals.sales_cyh.toLocaleString('es-GT')}</td>
                <td className="px-2 py-1.5 text-right font-black text-[var(--text)]">Q {weekly.totals.sales_sa.toLocaleString('es-GT')}</td>
                <td className="px-2 py-1.5 text-right font-black text-emerald-600 dark:text-emerald-400">Q {weekly.totals.sales_total.toLocaleString('es-GT')}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Mobile: stacked cards (one per week, larger inputs) */}
        <div className="sm:hidden space-y-2">
          {weekly.weeks.map((w) => (
            <div key={w.week} className="bg-[var(--surface)] border border-[var(--border)] rounded-md p-3" data-testid={`week-card-${w.week}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-black uppercase tracking-wide text-[var(--text-muted)]">Semana {w.week}</span>
                <span className="text-sm font-black text-emerald-600 dark:text-emerald-400">Q {w.sales_total.toLocaleString('es-GT')}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">CYH (Q)</span>
                  <input type="number" min="0" defaultValue={w.sales_cyh || ''} disabled={!weekly.can_edit} inputMode="numeric"
                    onBlur={(e) => updateWeek(w.week, 'sales_cyh', e.target.value)}
                    className="w-full mt-1 text-right bg-[var(--bg)] text-[var(--text)] border border-[var(--border)] focus:border-[var(--primary)] rounded px-2 py-2 text-sm font-bold disabled:opacity-60"
                    data-testid={`week-${w.week}-cyh-mobile`} placeholder="0" />
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">SA (Q)</span>
                  <input type="number" min="0" defaultValue={w.sales_sa || ''} disabled={!weekly.can_edit} inputMode="numeric"
                    onBlur={(e) => updateWeek(w.week, 'sales_sa', e.target.value)}
                    className="w-full mt-1 text-right bg-[var(--bg)] text-[var(--text)] border border-[var(--border)] focus:border-[var(--primary)] rounded px-2 py-2 text-sm font-bold disabled:opacity-60"
                    data-testid={`week-${w.week}-sa-mobile`} placeholder="0" />
                </label>
              </div>
            </div>
          ))}
          <div className="border-t-2 border-[var(--primary)] bg-[var(--surface-muted)] rounded-md p-3 mt-2">
            <p className="text-[10px] uppercase tracking-wide font-black text-[var(--text-muted)] mb-1">Total del mes</p>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div><span className="text-[var(--text-muted)]">CYH</span><br/><span className="font-black text-[var(--text)]">Q {weekly.totals.sales_cyh.toLocaleString('es-GT')}</span></div>
              <div><span className="text-[var(--text-muted)]">SA</span><br/><span className="font-black text-[var(--text)]">Q {weekly.totals.sales_sa.toLocaleString('es-GT')}</span></div>
              <div><span className="text-[var(--text-muted)]">Total</span><br/><span className="font-black text-emerald-600 dark:text-emerald-400">Q {weekly.totals.sales_total.toLocaleString('es-GT')}</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress bars */}
      {(form.target_cyh || form.target_sa) && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wide font-black text-[var(--text-muted)]">% Avance del mes</p>
          <ProgressRow label="CYH" pct={pctCyh} done={weekly.totals.sales_cyh} target={form.target_cyh} />
          <ProgressRow label="SA" pct={pctSa} done={weekly.totals.sales_sa} target={form.target_sa} />
          <ProgressRow label="TOTAL" pct={pctTotal} done={weekly.totals.sales_total} target={targetTotal} bold />
        </div>
      )}

      {/* Other goal fields collapsible — admin only */}
      {isAdminProp && (
      <details className="border border-[var(--border)] rounded-md p-2">
        <summary className="text-[11px] font-bold text-[var(--text-muted)] cursor-pointer">Metas de actividades (llamadas/visitas/cotizaciones)</summary>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
          <Field label="Llamadas/día"><Input type="number" min="0" value={form.daily_calls} onChange={(e) => setForm({ ...form, daily_calls: e.target.value })} placeholder="0" className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)]" data-testid="goal-daily-calls" /></Field>
          <Field label="Visitas/día"><Input type="number" min="0" value={form.daily_visits} onChange={(e) => setForm({ ...form, daily_visits: e.target.value })} placeholder="0" className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)]" data-testid="goal-daily-visits" /></Field>
          <Field label="Cotizac./día"><Input type="number" min="0" value={form.daily_quotes} onChange={(e) => setForm({ ...form, daily_quotes: e.target.value })} placeholder="0" className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)]" data-testid="goal-daily-quotes" /></Field>
          <Field label="Llamadas/mes"><Input type="number" min="0" value={form.monthly_calls} onChange={(e) => setForm({ ...form, monthly_calls: e.target.value })} placeholder="0" className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)]" data-testid="goal-monthly-calls" /></Field>
          <Field label="Visitas/mes"><Input type="number" min="0" value={form.monthly_visits} onChange={(e) => setForm({ ...form, monthly_visits: e.target.value })} placeholder="0" className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)]" data-testid="goal-monthly-visits" /></Field>
          <Field label="Cotizac./mes"><Input type="number" min="0" value={form.monthly_quotes} onChange={(e) => setForm({ ...form, monthly_quotes: e.target.value })} placeholder="0" className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)]" data-testid="goal-monthly-quotes" /></Field>
        </div>
      </details>
      )}

      {isAdminProp && (
      <div className="flex justify-end pt-2 border-t border-[var(--border)]">
        <Button onClick={handleSaveGoal} disabled={saving}
          className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
          data-testid="goal-save">
          <FloppyDisk size={16} className="mr-1" />
          {saving ? 'Guardando…' : (exists ? 'Actualizar metas' : 'Asignar metas')}
        </Button>
      </div>
      )}
    </div>
  );
};


const WeeklySalesPermToggle = ({ vendor }) => {
  const [checked, setChecked] = useState(!!vendor.can_manage_weekly_sales);
  const [saving, setSaving] = useState(false);
  const toggle = async () => {
    const newVal = !checked;
    setSaving(true);
    try {
      await api.put(`/admin/users/${vendor.id}/weekly-sales-permission`, { can_manage: newVal });
      setChecked(newVal);
      toast.success(newVal ? 'Permiso otorgado' : 'Permiso revocado');
    } catch (e) {
      toast.error('Error actualizando permiso');
    } finally {
      setSaving(false);
    }
  };
  return (
    <label className="flex items-center gap-2 cursor-pointer bg-[var(--surface)] border border-[var(--border)] rounded-md px-3 py-2 hover:bg-[var(--surface-muted)]" data-testid="weekly-sales-perm-toggle">
      <input type="checkbox" checked={checked} disabled={saving} onChange={toggle} className="accent-[var(--primary)]" />
      <div className="flex-1">
        <p className="text-xs font-bold text-[var(--text)]">Puede gestionar ventas semanales</p>
        <p className="text-[10px] text-[var(--text-muted)]">Llenar metas CYH/SA y ventas semanales de cualquier vendedor.</p>
      </div>
    </label>
  );
};

const ProgressRow = ({ label, pct, done, target, bold }) => {
  const color = pct === null ? 'bg-[var(--text-faint)]'
    : pct >= 100 ? 'bg-emerald-500'
    : pct >= 70 ? 'bg-blue-500'
    : pct >= 40 ? 'bg-amber-500' : 'bg-red-500';
  const textColor = pct === null ? 'text-[var(--text-faint)]'
    : pct >= 100 ? 'text-emerald-600 dark:text-emerald-400'
    : pct >= 70 ? 'text-blue-600 dark:text-blue-400'
    : pct >= 40 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400';
  return (
    <div data-testid={`progress-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between text-[10px] mb-0.5">
        <span className={`font-bold uppercase ${bold ? 'text-[var(--text)] text-xs' : 'text-[var(--text-muted)]'}`}>{label}</span>
        <span className={`font-black ${textColor}`}>
          Q {Number(done || 0).toLocaleString('es-GT')} / Q {Number(target || 0).toLocaleString('es-GT')}
          {' · '}{pct === null ? '—' : `${pct}%`}
        </span>
      </div>
      <div className="h-2 rounded-full bg-[var(--bg)] overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct === null ? 0 : Math.min(100, pct)}%` }} />
      </div>
    </div>
  );
};

const PermissionsTab = ({ vendor, onRoleChanged }) => {
  const [role, setRole] = useState(vendor.role || 'user');
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => { setRole(vendor.role || 'user'); }, [vendor]);

  const dirty = role !== vendor.role;
  const cfg = ROLES[role];

  const handleSave = async () => {
    if (role === 'admin' && !confirming) {
      setConfirming(true);
      return;
    }
    setSaving(true);
    try {
      const res = await api.put(`/admin/users/${vendor.id}/role`, { role });
      toast.success(`Rol actualizado: ${ROLES[res.data.old_role || vendor.role].label} → ${ROLES[role].label}`);
      onRoleChanged && onRoleChanged(role);
      setConfirming(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al actualizar rol');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[12px] p-4">
        <div className="text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)] mb-2">Rol actual</div>
        <div className="flex items-center gap-2">
          <span className="text-2xl">{ROLES[vendor.role || 'user'].icon}</span>
          <div>
            <div className="text-[var(--text)] font-black text-lg">{ROLES[vendor.role || 'user'].label}</div>
            <div className="text-xs text-[var(--text-muted)]">{ROLES[vendor.role || 'user'].description}</div>
          </div>
        </div>
      </div>

      <div>
        <div className="text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)] mb-2">Cambiar rol</div>
        <div className="grid grid-cols-3 gap-2">
          {Object.entries(ROLES).map(([k, c]) => (
            <button
              key={k}
              type="button"
              onClick={() => { setRole(k); setConfirming(false); }}
              className={`px-3 py-3 rounded-[10px] border-2 transition-all text-left ${role === k ? `bg-${c.color}-500/20 border-${c.color}-500/60` : 'bg-[var(--surface)] border-[var(--border)] hover:border-white/30'}`}
              data-testid={`role-option-${k}`}
            >
              <div className="text-xl mb-1">{c.icon}</div>
              <div className={`text-xs font-black ${role === k ? 'text-[var(--text)]' : 'text-[var(--text)]'}`}>{c.label}</div>
            </button>
          ))}
        </div>
      </div>

      <div className={`bg-[var(--surface)] border rounded-[12px] p-4 ${role === 'admin' ? 'border-amber-500/40' : 'border-[var(--border)]'}`}>
        <div className="text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)] mb-2">
          Permisos de {cfg.label}
        </div>
        <ul className="space-y-1.5">
          {cfg.permissions.map((p, i) => {
            const isNo = p.startsWith('NO');
            return (
              <li key={i} className="text-xs flex items-start gap-2">
                <span className={isNo ? 'text-red-400' : 'text-emerald-400'}>{isNo ? '✕' : '✓'}</span>
                <span className={isNo ? 'text-[var(--text-faint)]' : 'text-[var(--text)]'}>{p}</span>
              </li>
            );
          })}
        </ul>
      </div>

      {role === 'admin' && dirty && confirming && (
        <div className="bg-amber-500/10 border-2 border-amber-500/40 rounded-[12px] p-4">
          <div className="text-amber-300 font-black text-sm mb-2">⚠️ Confirma promoción a Administrador</div>
          <p className="text-xs text-[var(--text)] mb-3">
            <strong>{vendor.name}</strong> tendrá control TOTAL del CRM: podrá crear/eliminar negocios,
            cambiar credenciales Meta, asignar roles a otros (incluido el tuyo), exportar todos los datos
            y reactivar usuarios. Esta acción queda registrada en el audit log.
          </p>
          <p className="text-xs text-[var(--text-muted)] mb-3">
            Recomendado solo si vas a delegar el CRM o quieres dejar un sucesor de confianza.
          </p>
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="confirm-promote-admin-btn">
              {saving ? 'Promoviendo...' : `Sí, promover a ${vendor.name} a Admin`}
            </Button>
            <Button variant="outline" onClick={() => { setConfirming(false); setRole(vendor.role || 'user'); }}>
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {dirty && !confirming && (
        <Button onClick={handleSave} disabled={saving} className={`w-full ${role === 'admin' ? 'bg-amber-500 hover:bg-amber-600 text-black' : 'bg-[#1D4ED8] hover:bg-[#1E40AF]'}`} data-testid="save-role-btn">
          {saving ? 'Guardando...' : role === 'admin' ? '⚠️ Promover a Admin (requiere confirmación)' : `Guardar rol: ${cfg.label}`}
        </Button>
      )}
    </div>
  );
};

const Vendors = () => {
  const { selectedBusiness, hasTabPermission } = useOutletContext();
  const { user: currentUser, isAdmin } = useAuth();
  const canView = isAdmin || currentUser?.role === 'supervisor';
  // Tab visibility (granular sub-permission per Vendors module)
  const canSeeTab = (tab) => (hasTabPermission ? hasTabPermission('vendors', tab) : true);

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('cards');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [businessScope, setBusinessScope] = useState('current');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState('basico');
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deptList, setDeptList] = useState({ guatemala: [], special: [] });
  const [selectedDepts, setSelectedDepts] = useState([]);
  const [savingDepts, setSavingDepts] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [bulkBranchOpen, setBulkBranchOpen] = useState(false);
  const [goalProgress, setGoalProgress] = useState({}); // {user_id: {overall_percent, has_goals, breakdown}}
  const [loadingReport, setLoadingReport] = useState(false);
  const [nameHistory, setNameHistory] = useState([]);
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    api.get('/departments').then(r => setDeptList(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (view === 'report' && !reportData) {
      fetchReport();
    }
  }, [view]);

  const fetchReport = async () => {
    setLoadingReport(true);
    try {
      const r = await api.get('/admin/vendors/report');
      setReportData(r.data);
    } catch (e) {
      console.error(e);
      toast.error('Error al cargar reporte');
    } finally {
      setLoadingReport(false);
    }
  };

  const downloadReportPdf = async () => {
    try {
      const r = await api.get('/admin/vendors/report/pdf', { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const filename = `reporte-vendedores-${new Date().toISOString().slice(0,10)}.pdf`;
      const file = new File([blob], filename, { type: 'application/pdf' });
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Reporte de Vendedores' });
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('No se pudo generar el PDF');
    }
  };

  useEffect(() => {
    if (canView) {
      fetchVendors();
      api.get('/businesses').then(r => setBusinesses(r.data)).catch(() => {});
    }
  }, [statusFilter, businessScope, selectedBusiness]);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (businessScope === 'current' && selectedBusiness?.id) params.business_id = selectedBusiness.id;
      const response = await api.get('/admin/vendors', { params });
      setVendors(response.data);
      // Parallel: fetch goal progress for admins/supervisors
      if (isAdmin) {
        try {
          const gp = await api.get('/work-plan/goal-progress/all');
          const map = {};
          for (const v of gp.data.vendors || []) map[v.user_id] = v;
          setGoalProgress(map);
        } catch {
          // ignore
        }
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  const openVendor = (v) => {
    setSelectedVendor(v);
    setForm({
      name: v.name || '',
      photo_url: v.photo_url || '',
      dpi: v.dpi || '',
      phone_personal: v.phone_personal || '',
      whatsapp_reporte: v.whatsapp_reporte || '',
      email_personal: v.email_personal || '',
      birth_date: v.birth_date || '',
      address: v.address || '',
      hire_date: v.hire_date || '',
      position: v.position || '',
      commission_rate: v.commission_rate ?? '',
      employment_status: v.employment_status || 'active',
      emergency_contact_name: v.emergency_contact_name || '',
      emergency_contact_phone: v.emergency_contact_phone || '',
      notes: v.notes || '',
      branch: v.branch || '',
    });
    setSelectedDepts(v.departments || []);
    setNameHistory([]);
    setTab('basico');
    setIsOpen(true);
    // Fetch name change history for this vendor (admin/supervisor only)
    if (isAdmin || currentUser?.role === 'supervisor') {
      api.get(`/admin/users/${v.id}/name-history`)
        .then(r => setNameHistory(r.data || []))
        .catch(() => setNameHistory([]));
    }
  };

  const toggleDept = (d) => {
    setSelectedDepts((prev) => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  };

  const handleSaveDepartments = async () => {
    if (!selectedVendor) return;
    setSavingDepts(true);
    try {
      const response = await api.put(`/admin/users/${selectedVendor.id}/departments`, { departments: selectedDepts });
      toast.success('Departamentos actualizados');
      setVendors(prev => prev.map(v => v.id === selectedVendor.id ? { ...v, departments: response.data.departments } : v));
      setSelectedVendor((prev) => ({ ...prev, departments: response.data.departments }));
      setSelectedDepts(response.data.departments);
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Error al guardar departamentos';
      toast.error(detail);
    } finally {
      setSavingDepts(false);
    }
  };

  const handleSave = async () => {
    if (!selectedVendor) return;
    setSaving(true);
    try {
      const payload = { ...form };
      // 'name' is updated through a dedicated admin endpoint and is not part of profile fields
      delete payload.name;
      if (payload.commission_rate === '' || payload.commission_rate === null) {
        payload.commission_rate = null;
      } else {
        payload.commission_rate = Number(payload.commission_rate);
      }
      const response = await api.put(`/admin/users/${selectedVendor.id}/profile`, payload);
      toast.success('Ficha guardada');
      setVendors(prev => prev.map(v => v.id === selectedVendor.id ? { ...v, ...response.data } : v));
      setSelectedVendor((prev) => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error(error);
      toast.error('Error al guardar la ficha');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveName = async () => {
    if (!selectedVendor) return;
    const newName = (form.name || '').trim();
    if (!newName) {
      toast.error('El nombre no puede estar vacío');
      return;
    }
    if (newName === selectedVendor.name) {
      toast.info('El nombre no cambió');
      return;
    }
    if (!isAdmin) {
      toast.error('Solo administradores pueden editar el nombre');
      return;
    }
    setSavingName(true);
    try {
      await api.put(`/admin/users/${selectedVendor.id}/name`, { name: newName });
      toast.success(`Nombre actualizado: ${selectedVendor.name} → ${newName}`);
      setVendors(prev => prev.map(v => v.id === selectedVendor.id ? { ...v, name: newName } : v));
      setSelectedVendor((prev) => ({ ...prev, name: newName }));
      // Refresh history
      const r = await api.get(`/admin/users/${selectedVendor.id}/name-history`);
      setNameHistory(r.data || []);
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Error al actualizar nombre';
      toast.error(detail);
    } finally {
      setSavingName(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no puede pesar más de 5MB');
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Solo imágenes son permitidas');
      return;
    }
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/uploads/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = response.data.filename ? `/api/uploads/${response.data.filename}` : response.data.url;
      setForm((prev) => ({ ...prev, photo_url: url }));
      toast.success('Foto cargada — guarda para aplicar');
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error('Error al subir la foto');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  if (!canView) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-sm text-[var(--text-muted)]">Solo administradores y supervisores pueden ver vendedores.</p>
      </div>
    );
  }

  const filtered = vendors.filter(v => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (v.name || '').toLowerCase().includes(q)
      || (v.email || '').toLowerCase().includes(q)
      || (v.phone_personal || '').toLowerCase().includes(q)
      || (v.position || '').toLowerCase().includes(q);
  });

  const formatMoney = (n) => `Q ${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const formatDate = (s) => s ? new Date(s).toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const initials = (name) => (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  const isBirthdayToday = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  };

  const birthdayVendors = filtered.filter(v => isBirthdayToday(v.birth_date));

  return (
    <div className="min-h-screen bg-[var(--bg)] p-4 md:p-8" data-testid="vendors-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Users size={32} weight="fill" className="text-[#1D4ED8] drop-shadow-[0_0_12px_rgba(29,78,216,0.5)]" />
            <h1 className="text-2xl md:text-4xl font-black text-[var(--text)] tracking-tight">Vendedores</h1>
          </div>
          <p className="text-sm text-[var(--text-muted)] font-medium">Ficha completa del equipo, métricas en vivo, gestión de departamentos y zona.</p>
        </div>

        {/* Birthday banner */}
        {birthdayVendors.length > 0 && (
          <div className="mb-6 bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/30 rounded-[16px] p-4 flex items-center gap-3" data-testid="birthday-banner">
            <Cake size={28} weight="fill" className="text-pink-400 shrink-0" />
            <div>
              <p className="text-sm font-black text-[var(--text)]">🎉 ¡Cumpleaños hoy!</p>
              <p className="text-xs text-pink-300 mt-0.5 font-medium">
                {birthdayVendors.map(v => v.name).join(' · ')} — No olvides felicitar al equipo.
              </p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-[400px]">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-faint)]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar vendedor..."
              className="pl-9 bg-[var(--surface)] border-[var(--border)] text-[var(--text)] placeholder:text-[var(--text-faint)] h-9"
              data-testid="search-vendors"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs font-bold uppercase bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] px-3 py-2 rounded-[10px] focus:outline-none focus:border-[#1D4ED8] h-9"
            data-testid="filter-status"
          >
            <option value="all">Todos</option>
            <option value="active">Activos</option>
            <option value="vacation">Vacaciones</option>
            <option value="suspended">Suspendidos</option>
            <option value="inactive">Inactivos</option>
          </select>
          <select
            value={businessScope}
            onChange={(e) => setBusinessScope(e.target.value)}
            className="text-xs font-bold uppercase bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] px-3 py-2 rounded-[10px] focus:outline-none focus:border-[#1D4ED8] h-9"
            data-testid="filter-business-scope"
          >
            <option value="current">Negocio actual</option>
            <option value="all">Todos los negocios</option>
          </select>
          <div className="flex items-center gap-1 bg-[var(--surface)] border border-[var(--border)] rounded-[10px] p-1 ml-auto">
            <button
              onClick={() => setView('cards')}
              className={`p-1.5 rounded-[6px] transition-all ${view === 'cards' ? 'bg-[#1D4ED8] text-white' : 'text-[var(--text-muted)] hover:text-[var(--text)]'}`}
              data-testid="view-cards"
              title="Vista tarjetas"
            >
              <GridFour size={16} weight="bold" />
            </button>
            <button
              onClick={() => setView('table')}
              className={`p-1.5 rounded-[6px] transition-all ${view === 'table' ? 'bg-[#1D4ED8] text-white' : 'text-[var(--text-muted)] hover:text-[var(--text)]'}`}
              data-testid="view-table"
              title="Vista tabla"
            >
              <ListBullets size={16} weight="bold" />
            </button>
            <button
              onClick={() => setView('report')}
              className={`p-1.5 rounded-[6px] transition-all ${view === 'report' ? 'bg-[#1D4ED8] text-white' : 'text-[var(--text-muted)] hover:text-[var(--text)]'}`}
              data-testid="view-report"
              title="Reporte completo"
            >
              <FileText size={16} weight="bold" />
            </button>
          </div>
          {isAdmin && (
            <button
              onClick={() => setBulkBranchOpen(true)}
              className="text-xs font-bold uppercase bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white px-3 py-2 rounded-[10px] h-9 flex items-center gap-1.5 ml-auto md:ml-0"
              data-testid="btn-bulk-branch"
              title="Asignar sucursal a varios vendedores"
            >
              <House size={14} weight="bold" />
              Asignar sucursal
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center text-[var(--text-muted)] py-12">Cargando vendedores...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[16px] p-12 text-center" data-testid="vendors-empty">
            <Users size={48} weight="duotone" className="text-[var(--text-faint)] mx-auto mb-3" />
            <p className="text-[var(--text-muted)] font-medium">No hay vendedores que coincidan con los filtros</p>
          </div>
        ) : view === 'cards' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(v => {
              const status = STATUS_CONFIG[v.employment_status || 'active'];
              return (
                <div
                  key={v.id}
                  onClick={() => openVendor(v)}
                  className="group bg-[var(--surface)] border border-[var(--border)] hover:border-[#1D4ED8]/50 rounded-[16px] p-5 cursor-pointer transition-all hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(29,78,216,0.15)]"
                  data-testid={`vendor-card-${v.id}`}
                >
                  <div className="flex items-start gap-3 mb-4">
                    {v.photo_url ? (
                      <img src={v.photo_url} alt={v.name} className="w-14 h-14 rounded-full object-cover border-2 border-[var(--border)]" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#1D4ED8] to-[#7C3AED] flex items-center justify-center text-white font-black text-lg">
                        {initials(v.name)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-black text-[var(--text)] truncate group-hover:text-[#1D4ED8] transition-colors">{v.name}</h3>
                      <p className="text-xs text-[var(--text-muted)] truncate font-medium">{v.position || (v.role === 'supervisor' ? 'Supervisor' : 'Vendedor')}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                        <span className="text-[10px] uppercase tracking-wide font-bold text-[var(--text-muted)]">{status.label}</span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-[var(--bg)] border border-[var(--border)] rounded-[10px] p-2">
                      <div className="flex items-center gap-1 text-[9px] uppercase tracking-wide text-[var(--text-faint)] font-bold mb-0.5">
                        <CurrencyDollar size={10} weight="bold" />
                        Mes
                      </div>
                      <p className="text-sm font-black text-emerald-400">{formatMoney(v.metrics?.revenue_month)}</p>
                    </div>
                    <div className="bg-[var(--bg)] border border-[var(--border)] rounded-[10px] p-2">
                      <div className="flex items-center gap-1 text-[9px] uppercase tracking-wide text-[var(--text-faint)] font-bold mb-0.5">
                        <ShoppingCart size={10} weight="bold" />
                        Pedidos
                      </div>
                      <p className="text-sm font-black text-[var(--text)]">{v.metrics?.orders_month || 0}</p>
                    </div>
                  </div>
                  {(v.departments?.length > 0) && (
                    <div className="flex flex-wrap gap-1">
                      {v.departments.slice(0, 3).map(d => (
                        <span key={d} className="text-[9px] font-bold px-1.5 py-0.5 bg-[#1D4ED8]/15 text-[#1D4ED8] border border-[#1D4ED8]/30 rounded">{d}</span>
                      ))}
                      {v.departments.length > 3 && (
                        <span className="text-[9px] font-bold text-[var(--text-faint)]">+{v.departments.length - 3}</span>
                      )}
                    </div>
                  )}
                  {/* Goal completion progress (admin only) */}
                  {isAdmin && goalProgress[v.id] && (
                    <GoalProgressBar progress={goalProgress[v.id]} />
                  )}
                </div>
              );
            })}
          </div>
        ) : view === 'table' ? (
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[16px] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="vendors-table">
                <thead className="bg-[var(--bg)]">
                  <tr>
                    <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)]">Vendedor</th>
                    <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)]">Username</th>
                    <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)]">Cargo</th>
                    <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)]">Estado</th>
                    <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)]">Departamentos</th>
                    <th className="px-4 py-3 text-right text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)]">Mes</th>
                    <th className="px-4 py-3 text-right text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)]">Pedidos</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(v => {
                    const status = STATUS_CONFIG[v.employment_status || 'active'];
                    return (
                      <tr key={v.id} className="border-t border-[var(--border)] hover:bg-[var(--surface-muted)] cursor-pointer" onClick={() => openVendor(v)} data-testid={`vendor-row-${v.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {v.photo_url ? (
                              <img src={v.photo_url} alt={v.name} className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1D4ED8] to-[#7C3AED] flex items-center justify-center text-white font-black text-xs">
                                {initials(v.name)}
                              </div>
                            )}
                            <span className="text-sm font-bold text-[var(--text)]">{v.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-[var(--text-muted)] font-mono">{v.username || '—'}</td>
                        <td className="px-4 py-3 text-xs text-[var(--text)]">{v.position || '—'}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase">
                            <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                            <span className="text-[var(--text)]">{status.label}</span>
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {(v.departments || []).slice(0, 2).map(d => (
                              <span key={d} className="text-[9px] font-bold px-1.5 py-0.5 bg-[#1D4ED8]/15 text-[#1D4ED8] border border-[#1D4ED8]/30 rounded">{d}</span>
                            ))}
                            {(v.departments?.length || 0) > 2 && (
                              <span className="text-[9px] text-[var(--text-faint)] font-bold">+{v.departments.length - 2}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-bold text-emerald-400 text-right">{formatMoney(v.metrics?.revenue_month)}</td>
                        <td className="px-4 py-3 text-sm font-bold text-[var(--text)] text-right">{v.metrics?.orders_month || 0}</td>
                        <td className="px-4 py-3 text-right">
                          <PencilSimple size={14} weight="bold" className="text-[var(--text-muted)] inline" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[16px] overflow-hidden" data-testid="vendors-report">
            <div className="p-4 border-b border-[var(--border)] flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <FileText size={18} weight="bold" className="text-[#1D4ED8]" />
                <h3 className="text-sm font-black uppercase tracking-wider text-[var(--text)]">Reporte Completo</h3>
                <span className="text-xs text-[var(--text-muted)] font-medium">{reportData?.vendors?.length || 0} vendedores</span>
              </div>
              <Button onClick={downloadReportPdf} className="bg-emerald-600 hover:bg-emerald-500" data-testid="download-report-pdf-btn">
                <FilePdf size={14} weight="bold" className="mr-1" /> Descargar PDF
              </Button>
            </div>
            {loadingReport ? (
              <div className="text-center text-[var(--text-muted)] py-12">Cargando reporte...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[var(--bg)]">
                    <tr>
                      <th className="px-3 py-2 text-left text-[9px] font-black uppercase tracking-wider text-[var(--text-muted)]">Vendedor</th>
                      <th className="px-3 py-2 text-left text-[9px] font-black uppercase tracking-wider text-[var(--text-muted)]">Cargo</th>
                      <th className="px-3 py-2 text-left text-[9px] font-black uppercase tracking-wider text-[var(--text-muted)]">Estado</th>
                      <th className="px-3 py-2 text-left text-[9px] font-black uppercase tracking-wider text-[var(--text-muted)]">Negocios</th>
                      <th className="px-3 py-2 text-left text-[9px] font-black uppercase tracking-wider text-[var(--text-muted)]">Departamentos</th>
                      <th className="px-3 py-2 text-left text-[9px] font-black uppercase tracking-wider text-[var(--text-muted)]">Permisos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(reportData?.vendors || []).map(v => {
                      const status = STATUS_CONFIG[v.employment_status || 'active'];
                      return (
                        <tr key={v.id} className="border-t border-[var(--border)] hover:bg-[var(--surface-muted)]">
                          <td className="px-3 py-2 text-xs font-bold text-[var(--text)] align-top">{v.name}</td>
                          <td className="px-3 py-2 text-xs text-[var(--text)] align-top">{v.position || (v.role === 'supervisor' ? 'Supervisor' : 'Vendedor')}</td>
                          <td className="px-3 py-2 align-top">
                            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase">
                              <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                              <span className="text-[var(--text)]">{status.label}</span>
                            </span>
                          </td>
                          <td className="px-3 py-2 align-top">
                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                              {(v.businesses || []).map(b => (
                                <span key={b} className="text-[9px] font-bold px-1.5 py-0.5 bg-[#1D4ED8]/10 text-[#1D4ED8] dark:bg-[#1D4ED8]/20 dark:text-[#93C5FD] border border-[#1D4ED8]/40 font-bold rounded">{b}</span>
                              ))}
                              {(!v.businesses || v.businesses.length === 0) && <span className="text-[10px] text-[var(--text-faint)]">—</span>}
                            </div>
                          </td>
                          <td className="px-3 py-2 align-top">
                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                              {(v.departments || []).map(d => (
                                <span key={d} className="text-[9px] font-bold px-1.5 py-0.5 bg-[#1D4ED8]/15 text-[#1D4ED8] border border-[#1D4ED8]/30 rounded">{d}</span>
                              ))}
                              {(!v.departments || v.departments.length === 0) && <span className="text-[10px] text-[var(--text-faint)]">—</span>}
                            </div>
                          </td>
                          <td className="px-3 py-2 align-top">
                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                              {(v._permissions_aggregated || []).map(p => (
                                <span key={p} className="text-[9px] font-bold px-1.5 py-0.5 bg-violet-500/15 text-violet-300 border border-violet-500/30 rounded">{p}</span>
                              ))}
                              {(!v._permissions_aggregated || v._permissions_aggregated.length === 0) && <span className="text-[10px] text-[var(--text-faint)]">—</span>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Vendor Profile Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="w-[96vw] sm:w-auto max-w-3xl bg-[var(--bg)] border-[var(--border)] text-[var(--text)] max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="vendor-detail-dialog">
          {selectedVendor && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  {selectedVendor.photo_url ? (
                    <img src={selectedVendor.photo_url} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-[var(--border)]" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1D4ED8] to-[#7C3AED] flex items-center justify-center text-white font-black text-xl">
                      {initials(selectedVendor.name)}
                    </div>
                  )}
                  <div>
                    <DialogTitle className="text-[var(--text)] text-xl">{selectedVendor.name}</DialogTitle>
                    <DialogDescription className="text-[var(--text-muted)]">
                      {selectedVendor.position || (selectedVendor.role === 'supervisor' ? 'Supervisor' : 'Vendedor')} · {STATUS_CONFIG[selectedVendor.employment_status || 'active'].label}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="bg-[var(--surface)] border border-[var(--border)] flex w-full overflow-x-auto justify-start sm:justify-center gap-1 no-scrollbar">
                  {canSeeTab('basico') && <TabsTrigger value="basico" data-testid="tab-basico">Básicos</TabsTrigger>}
                  {canSeeTab('laboral') && <TabsTrigger value="laboral" data-testid="tab-laboral">Laborales</TabsTrigger>}
                  {canSeeTab('deptos') && <TabsTrigger value="deptos" data-testid="tab-deptos">Deptos</TabsTrigger>}
                  {canSeeTab('emergencia') && <TabsTrigger value="emergencia" data-testid="tab-emergencia">Emergencia</TabsTrigger>}
                  {canSeeTab('metricas') && <TabsTrigger value="metricas" data-testid="tab-metricas">Métricas</TabsTrigger>}
                  {(isAdmin || canSeeTab('plan')) && <TabsTrigger value="plan" data-testid="tab-plan">Plan</TabsTrigger>}
                  {canSeeTab('notas') && <TabsTrigger value="notas" data-testid="tab-notas">Notas</TabsTrigger>}
                  {isAdmin && <TabsTrigger value="permisos" data-testid="tab-permisos">🔐 Permisos</TabsTrigger>}
                </TabsList>

                <TabsContent value="basico" className="space-y-3 pt-4">
                  <Field label="Nombre visible (dashboard, mensajes, ranking)" icon={UserCircle}>
                    <div className="flex gap-2">
                      <Input
                        value={form.name || ''}
                        onChange={e => setForm({...form, name: e.target.value})}
                        placeholder="Ej. Juan Pérez"
                        disabled={!isAdmin}
                        className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] flex-1"
                        data-testid="input-vendor-name"
                      />
                      {isAdmin && (
                        <Button
                          onClick={handleSaveName}
                          disabled={savingName || (form.name || '').trim() === selectedVendor.name}
                          className="bg-[#1D4ED8] hover:bg-[#1E40AF] shrink-0"
                          data-testid="save-vendor-name-btn"
                        >
                          {savingName ? '...' : 'Aplicar'}
                        </Button>
                      )}
                    </div>
                    {!isAdmin && (
                      <p className="text-[10px] text-[var(--text-faint)] mt-1">Solo el admin puede editar el nombre desde aquí.</p>
                    )}
                    {nameHistory.length > 0 && (
                      <div className="mt-2 bg-[var(--bg)] border border-[var(--border)] rounded-[8px] p-2">
                        <div className="text-[9px] font-black uppercase tracking-wider text-[var(--text-faint)] mb-1.5 flex items-center gap-1">
                          <ClockCounterClockwise size={10} weight="bold" />
                          Historial de cambios ({nameHistory.length})
                        </div>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {nameHistory.map((h, idx) => (
                            <div key={idx} className="text-[10px] text-[var(--text)] flex items-center justify-between gap-2" data-testid={`name-history-${idx}`}>
                              <span className="truncate">
                                {h.action === 'role_change' ? (
                                  <>
                                    <span className="text-amber-400 mr-1">🔐</span>
                                    <span className="text-[var(--text-faint)]">{h.old_role}</span>
                                    <span className="text-[var(--text-faint)] mx-1">→</span>
                                    <span className="font-bold text-amber-300">{h.new_role}</span>
                                  </>
                                ) : (
                                  <>
                                    <span className="text-[var(--text-faint)]">{h.old_name || '(sin nombre)'}</span>
                                    <span className="text-[var(--text-faint)] mx-1">→</span>
                                    <span className="font-bold text-emerald-400">{h.new_name}</span>
                                  </>
                                )}
                              </span>
                              <span className="text-[9px] text-[var(--text-faint)] shrink-0">
                                {h.changed_by === 'admin' ? `Admin: ${h.changed_by_name || ''}` : 'Auto'} · {new Date(h.timestamp).toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: '2-digit' })}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </Field>
                  <Field label="Foto de perfil">
                    <div className="flex items-center gap-3">
                      {form.photo_url ? (
                        <img src={form.photo_url.startsWith('http') || form.photo_url.startsWith('/') ? form.photo_url : `/api/uploads/${form.photo_url}`} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-[var(--border)] shrink-0" />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1D4ED8] to-[#7C3AED] flex items-center justify-center text-white font-black text-lg shrink-0">
                          {initials(selectedVendor.name)}
                        </div>
                      )}
                      <div className="flex-1 space-y-2">
                        <label className="block">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handlePhotoUpload}
                            disabled={uploadingPhoto}
                            className="hidden"
                            data-testid="upload-photo-input"
                          />
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-[8px] bg-[#1D4ED8] hover:bg-[#1E40AF] text-white cursor-pointer transition-colors disabled:opacity-50">
                            <IdentificationCard size={14} weight="bold" />
                            {uploadingPhoto ? 'Subiendo...' : 'Subir foto'}
                          </span>
                        </label>
                        {form.photo_url && (
                          <button
                            type="button"
                            onClick={() => setForm({...form, photo_url: ''})}
                            className="text-[10px] text-red-400 hover:text-red-300 font-bold uppercase tracking-wider ml-2"
                            data-testid="remove-photo-btn"
                          >
                            Eliminar foto
                          </button>
                        )}
                      </div>
                    </div>
                  </Field>
                  <Field label="DPI / Identificación" icon={IdentificationCard}>
                    <Input value={form.dpi} onChange={e => setForm({...form, dpi: e.target.value})} placeholder="0000 00000 0000" className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)]" data-testid="input-dpi" />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Teléfono personal" icon={Phone}>
                      <Input value={form.phone_personal} onChange={e => setForm({...form, phone_personal: e.target.value})} placeholder="+502 ..." className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)]" data-testid="input-phone_personal" />
                    </Field>
                    <Field label="Email personal" icon={EnvelopeSimple}>
                      <Input value={form.email_personal} onChange={e => setForm({...form, email_personal: e.target.value})} placeholder="..." className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)]" data-testid="input-email_personal" />
                    </Field>
                  </div>
                  <Field label="WhatsApp para reporte semanal de ventas" icon={Phone}>
                    <Input value={form.whatsapp_reporte} onChange={e => setForm({...form, whatsapp_reporte: e.target.value})} placeholder="8 dígitos (se agrega 502) o 502XXXXXXXX" className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)]" data-testid="input-whatsapp_reporte" />
                    <p className="text-[10px] text-[var(--text-muted)] mt-1">Si tiene número, recibirá cada lunes 9 AM su reporte de ventas de la semana anterior por WhatsApp.</p>
                  </Field>
                  <Field label="Fecha de nacimiento" icon={Cake}>
                    <Input type="date" value={form.birth_date} onChange={e => setForm({...form, birth_date: e.target.value})} className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)]" data-testid="input-birth_date" />
                  </Field>
                  <Field label="Dirección" icon={House}>
                    <Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="Dirección completa" className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)]" data-testid="input-address" />
                  </Field>
                </TabsContent>

                <TabsContent value="laboral" className="space-y-3 pt-4">
                  <Field label="Fecha de ingreso" icon={ClockCounterClockwise}>
                    <Input type="date" value={form.hire_date} onChange={e => setForm({...form, hire_date: e.target.value})} className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)]" data-testid="input-hire_date" />
                  </Field>
                  <Field label="Cargo / Posición" icon={Briefcase}>
                    <select value={form.position} onChange={e => setForm({...form, position: e.target.value})} className="w-full bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="input-position">
                      <option value="">Seleccionar...</option>
                      {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </Field>
                  <Field label="Comisión %" icon={CurrencyDollar}>
                    <Input type="number" step="0.5" value={form.commission_rate} onChange={e => setForm({...form, commission_rate: e.target.value})} placeholder="5" className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)]" data-testid="input-commission_rate" />
                  </Field>
                  <Field label="Sucursal" icon={House}>
                    <select value={form.branch} onChange={e => setForm({...form, branch: e.target.value})} className="w-full bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="input-branch">
                      <option value="">Sin sucursal</option>
                      <option value="Central">Central</option>
                      <option value="Quetzaltenango">Quetzaltenango</option>
                      <option value="Huehuetenango">Huehuetenango</option>
                      <option value="Chichicastenango">Chichicastenango</option>
                      <option value="Chiquimula">Chiquimula</option>
                    </select>
                  </Field>
                  {isAdmin && selectedVendor.role === 'supervisor' && (
                    <Field label="Permiso especial">
                      <WeeklySalesPermToggle vendor={selectedVendor} />
                    </Field>
                  )}
                  <Field label="Estado">
                    <div className="grid grid-cols-4 gap-2">
                      {Object.entries(STATUS_CONFIG).map(([k, s]) => (
                        <button
                          key={k}
                          type="button"
                          onClick={() => setForm({...form, employment_status: k})}
                          className={`text-xs font-bold px-3 py-2 rounded-[8px] border transition-all ${form.employment_status === k ? 'bg-[#1D4ED8] border-[#1D4ED8] text-white' : 'bg-[var(--surface)] border-[var(--border)] text-[var(--text-muted)] hover:border-[#1D4ED8]/50'}`}
                          data-testid={`status-${k}`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </Field>
                  <Field label="Departamentos asignados" icon={MapPin}>
                    <div className="text-xs text-[var(--text-muted)] font-medium">
                      {(selectedVendor.departments?.length || 0) > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {selectedVendor.departments.map(d => (
                            <span key={d} className="text-[10px] font-bold px-2 py-1 bg-[#1D4ED8]/20 text-[#1D4ED8] border border-[#1D4ED8]/40 rounded">{d}</span>
                          ))}
                        </div>
                      ) : (
                        <span>Ninguno asignado. Ve a la pestaña <strong className="text-[var(--text)]">Deptos</strong> para asignar.</span>
                      )}
                    </div>
                  </Field>
                  <Field label="Negocios asignados">
                    <div className="text-xs text-[var(--text-muted)] font-medium">
                      {(selectedVendor.businesses?.length || 0) > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {selectedVendor.businesses.map(bid => {
                            const biz = businesses.find(b => b.id === bid);
                            return <span key={bid} className="text-[10px] font-bold px-2 py-1 bg-[#1D4ED8]/10 text-[#1D4ED8] dark:bg-[#1D4ED8]/20 dark:text-[#93C5FD] border border-[#1D4ED8]/40 font-bold rounded">{biz?.name || bid}</span>;
                          })}
                        </div>
                      ) : (
                        <span>Sin permisos. Ve a <strong className="text-[var(--text)]">Admin → Permisos</strong>.</span>
                      )}
                    </div>
                  </Field>
                </TabsContent>

                <TabsContent value="deptos" className="space-y-4 pt-4">
                  <div className="bg-[var(--surface)] border border-[#1D4ED8]/30 rounded-[10px] p-3">
                    <p className="text-xs text-[var(--text)] font-medium">
                      <strong className="text-[var(--text)]">¿Cómo funciona?</strong> Marca los departamentos que este vendedor atenderá.
                      Si varios vendedores comparten un departamento, las conversaciones se reparten automáticamente por <strong className="text-[#1D4ED8]">round-robin</strong>.
                    </p>
                  </div>

                  <div>
                    <p className="text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)] mb-2">🇬🇹 Guatemala</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {deptList.guatemala.map(d => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => toggleDept(d)}
                          className={`text-xs px-3 py-2 rounded-[8px] font-bold border transition-all text-left ${
                            selectedDepts.includes(d)
                              ? 'bg-[#1D4ED8] border-[#1D4ED8] text-white shadow-[0_0_10px_rgba(29,78,216,0.4)]'
                              : 'bg-[var(--bg)] border-[var(--border)] text-[var(--text)] hover:border-[#1D4ED8]/50'
                          }`}
                          data-testid={`vendor-dept-${d}`}
                        >
                          {selectedDepts.includes(d) ? '✓ ' : ''}{d}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)] mb-2">🌎 Otros</p>
                    <div className="grid grid-cols-2 gap-2">
                      {deptList.special.map(d => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => toggleDept(d)}
                          className={`text-xs px-3 py-2 rounded-[8px] font-bold border transition-all text-left ${
                            selectedDepts.includes(d)
                              ? 'bg-[#7C3AED] border-[#7C3AED] text-white shadow-[0_0_10px_rgba(124,58,237,0.4)]'
                              : 'bg-[var(--bg)] border-[var(--border)] text-[var(--text)] hover:border-[#7C3AED]/50'
                          }`}
                          data-testid={`vendor-dept-special-${d}`}
                        >
                          {selectedDepts.includes(d) ? '✓ ' : ''}{d}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-3 border-t border-[var(--border)]">
                    <span className="text-xs text-[var(--text-muted)] font-bold">
                      {selectedDepts.length} departamento{selectedDepts.length !== 1 ? 's' : ''} seleccionado{selectedDepts.length !== 1 ? 's' : ''}
                    </span>
                    <Button
                      onClick={handleSaveDepartments}
                      disabled={savingDepts}
                      className="bg-[#1D4ED8] hover:bg-[#1E40AF]"
                      data-testid="save-vendor-depts-btn"
                    >
                      <FloppyDisk size={16} weight="bold" className="mr-1" />
                      {savingDepts ? 'Guardando...' : 'Guardar departamentos'}
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="emergencia" className="space-y-3 pt-4">
                  <Field label="Contacto de emergencia (nombre)" icon={Users}>
                    <Input value={form.emergency_contact_name} onChange={e => setForm({...form, emergency_contact_name: e.target.value})} placeholder="Nombre completo" className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)]" data-testid="input-emergency_contact_name" />
                  </Field>
                  <Field label="Teléfono de emergencia" icon={Phone}>
                    <Input value={form.emergency_contact_phone} onChange={e => setForm({...form, emergency_contact_phone: e.target.value})} placeholder="+502 ..." className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)]" data-testid="input-emergency_contact_phone" />
                  </Field>
                </TabsContent>

                <TabsContent value="metricas" className="space-y-4 pt-4">
                  <MetasDiariasRapidas vendor={selectedVendor} />
                  {/* Ventas */}
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-faint)] mb-2">Ventas (mes actual)</p>
                    <div className="grid grid-cols-2 gap-3">
                      <MetricCard icon={CurrencyDollar} label="Dinero este mes" value={formatMoney(selectedVendor.metrics?.revenue_month)} color="emerald" />
                      <MetricCard icon={ShoppingCart} label="Pedidos este mes" value={selectedVendor.metrics?.orders_month || 0} color="blue" />
                    </div>
                  </div>

                  {/* Conversaciones */}
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-faint)] mb-2">Conversaciones</p>
                    <div className="grid grid-cols-2 gap-3">
                      <MetricCard icon={ChatCircleDots} label="Asignadas (total)" value={selectedVendor.metrics?.conv_assigned_total || 0} color="blue" />
                      <MetricCard icon={CheckCircle} label="Contestadas (total)" value={selectedVendor.metrics?.conv_answered_total || 0} color="emerald" />
                    </div>
                  </div>

                  {/* Desglose por plataforma */}
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-faint)] mb-2">Por plataforma</p>
                    <div className="space-y-2" data-testid="vendor-conv-platform-breakdown">
                      <PlatformRow
                        icon={WhatsappLogo}
                        color="#25D366"
                        label="WhatsApp"
                        assigned={selectedVendor.metrics?.conv_assigned_whatsapp || 0}
                        answered={selectedVendor.metrics?.conv_answered_whatsapp || 0}
                      />
                      <PlatformRow
                        icon={MessengerLogo}
                        color="#0084FF"
                        label="Messenger"
                        assigned={selectedVendor.metrics?.conv_assigned_messenger || 0}
                        answered={selectedVendor.metrics?.conv_answered_messenger || 0}
                      />
                    </div>
                  </div>

                  {/* Otros */}
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-faint)] mb-2">Otros</p>
                    <div className="grid grid-cols-2 gap-3">
                      <MetricCard icon={Trophy} label="Departamentos" value={selectedVendor.departments?.length || 0} color="yellow" />
                      <MetricCard icon={ClockCounterClockwise} label="Última actividad" value={selectedVendor.last_activity ? formatDate(selectedVendor.last_activity) : 'Sin actividad'} color="slate" />
                    </div>
                  </div>

                  <p className="text-xs text-[var(--text-faint)] text-center pt-2">Ver ranking completo en el módulo <strong className="text-[var(--text)]">🏆 Top Vendedores</strong></p>
                </TabsContent>

                {(isAdmin || canSeeTab('plan')) && (
                  <TabsContent value="plan" className="pt-4" data-testid="plan-content">
                    <GoalsTab vendor={selectedVendor} isAdmin={isAdmin} />
                  </TabsContent>
                )}

                <TabsContent value="notas" className="pt-4">
                  <Textarea
                    value={form.notes}
                    onChange={e => setForm({...form, notes: e.target.value})}
                    rows={8}
                    placeholder="Observaciones, advertencias, reconocimientos..."
                    className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)]"
                    data-testid="input-notes"
                  />
                </TabsContent>

                {isAdmin && (
                  <TabsContent value="permisos" className="pt-4 space-y-4" data-testid="permisos-content">
                    <PermissionsTab vendor={selectedVendor} onRoleChanged={(newRole) => {
                      setVendors(prev => prev.map(v => v.id === selectedVendor.id ? { ...v, role: newRole } : v));
                      setSelectedVendor((prev) => ({ ...prev, role: newRole }));
                    }} />
                  </TabsContent>
                )}
              </Tabs>

              <div className="flex justify-end gap-2 pt-3 border-t border-[var(--border)]">
                <Button variant="outline" onClick={() => setIsOpen(false)} disabled={saving}>
                  <X size={16} weight="bold" className="mr-1" /> Cerrar
                </Button>
                <Button onClick={handleSave} disabled={saving} className="bg-[#1D4ED8] hover:bg-[#1E40AF]" data-testid="save-vendor-btn">
                  <FloppyDisk size={16} weight="bold" className="mr-1" /> {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Branch Modal (admin only) */}
      <BulkBranchModal
        open={bulkBranchOpen}
        onClose={() => setBulkBranchOpen(false)}
        vendors={vendors}
        onSaved={() => {
          setBulkBranchOpen(false);
          fetchVendors();
        }}
      />
    </div>
  );
};

const BulkBranchModal = ({ open, onClose, vendors, onSaved }) => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [branch, setBranch] = useState('');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  // Reset when opened
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set());
      setBranch('');
      setSearch('');
    }
  }, [open]);

  const BRANCHES = ['Central', 'Quetzaltenango', 'Huehuetenango', 'Chichicastenango', 'Chiquimula'];
  const activeVendors = (vendors || []).filter((v) =>
    v.active !== false && (v.role === 'user' || v.role === 'supervisor')
  );
  const filtered = activeVendors.filter((v) =>
    v.name?.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === filtered.length && filtered.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((v) => v.id)));
    }
  };

  const handleSave = async () => {
    if (selectedIds.size === 0) {
      toast.error('Selecciona al menos un vendedor');
      return;
    }
    if (!branch) {
      toast.error('Selecciona una sucursal');
      return;
    }
    setSaving(true);
    try {
      const res = await api.put('/work-plan/users/bulk-branch', {
        user_ids: Array.from(selectedIds),
        branch,
      });
      toast.success(`${res.data.modified} vendedor${res.data.modified === 1 ? '' : 'es'} asignado${res.data.modified === 1 ? '' : 's'} a ${branch}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-[var(--bg)] border-[var(--border)] text-[var(--text)] max-h-[88vh] overflow-y-auto" data-testid="bulk-branch-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <House size={20} weight="bold" className="text-[var(--primary)]" />
            Asignar sucursal masivamente
          </DialogTitle>
          <DialogDescription className="text-xs text-[var(--text-muted)]">
            Selecciona vendedores y la sucursal a asignar. Sobrescribe el valor actual.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field label="Sucursal" icon={House}>
            <select
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              className="w-full bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] rounded-md px-3 py-2 text-sm"
              data-testid="bulk-branch-select"
            >
              <option value="">— Selecciona —</option>
              {BRANCHES.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </Field>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Input
                placeholder="Buscar vendedor..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] h-8 max-w-[300px]"
                data-testid="bulk-branch-search"
              />
              <button
                onClick={toggleAll}
                className="text-[11px] font-bold text-[var(--primary)] hover:underline"
                data-testid="bulk-branch-toggle-all"
              >
                {selectedIds.size === filtered.length && filtered.length > 0 ? 'Quitar todos' : 'Seleccionar todos'}
              </button>
            </div>
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[10px] max-h-[280px] overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="p-4 text-xs text-[var(--text-muted)] text-center">Sin resultados</p>
              ) : (
                filtered.map((v) => (
                  <label
                    key={v.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-[var(--surface-muted)] cursor-pointer border-b border-[var(--border)] last:border-b-0"
                    data-testid={`bulk-branch-row-${v.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(v.id)}
                      onChange={() => toggle(v.id)}
                      className="accent-[var(--primary)]"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-[var(--text)] truncate">{v.name}</p>
                      <p className="text-[10px] text-[var(--text-muted)]">
                        {v.role} · Sucursal actual: <strong className="text-[var(--text)]">{v.branch || 'sin asignar'}</strong>
                      </p>
                    </div>
                  </label>
                ))
              )}
            </div>
            <p className="text-[10px] text-[var(--text-faint)] mt-1.5 font-bold">
              {selectedIds.size} seleccionado{selectedIds.size === 1 ? '' : 's'} · {filtered.length} mostrado{filtered.length === 1 ? '' : 's'}
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--border)]">
            <Button variant="ghost" onClick={onClose} data-testid="bulk-branch-cancel">
              <X size={16} className="mr-1" />Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || selectedIds.size === 0 || !branch}
              className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
              data-testid="bulk-branch-save"
            >
              <FloppyDisk size={16} className="mr-1" />
              {saving ? 'Guardando…' : `Asignar a ${selectedIds.size}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const GoalProgressBar = ({ progress }) => {
  const overall = progress.overall_percent;
  const has = progress.has_goals;
  const bd = progress.breakdown || {};

  if (!has) {
    return (
      <div className="mt-3 pt-3 border-t border-[var(--border)]" data-testid="goal-progress-empty">
        <p className="text-[10px] text-[var(--text-faint)] italic">📋 Sin plan asignado este mes</p>
      </div>
    );
  }

  const colorClass = overall === null
    ? 'bg-[var(--text-faint)]'
    : overall >= 100 ? 'bg-emerald-500'
    : overall >= 70 ? 'bg-blue-500'
    : overall >= 40 ? 'bg-amber-500'
    : 'bg-red-500';

  const textColor = overall === null
    ? 'text-[var(--text-faint)]'
    : overall >= 100 ? 'text-emerald-600 dark:text-emerald-400'
    : overall >= 70 ? 'text-blue-600 dark:text-blue-400'
    : overall >= 40 ? 'text-amber-600 dark:text-amber-400'
    : 'text-red-600 dark:text-red-400';

  const tooltip = ['calls', 'visits', 'quotes', 'sales']
    .filter(k => bd[k] !== null && bd[k] !== undefined)
    .map(k => {
      const labels = { calls: 'Llamadas', visits: 'Visitas', quotes: 'Cotiz.', sales: 'Ventas' };
      return `${labels[k]}: ${bd[k]}%`;
    }).join(' · ');

  return (
    <div className="mt-3 pt-3 border-t border-[var(--border)]" data-testid="goal-progress-bar" title={tooltip}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] uppercase tracking-wide text-[var(--text-faint)] font-bold">Cumplimiento mes</span>
        <span className={`text-xs font-black ${textColor}`}>
          {overall === null ? '—' : `${overall}%`}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-[var(--bg)] overflow-hidden">
        <div
          className={`h-full ${colorClass} transition-all duration-500`}
          style={{ width: `${overall === null ? 0 : Math.min(100, overall)}%` }}
        />
      </div>
      <div className="grid grid-cols-4 gap-1 mt-1.5">
        {['calls', 'visits', 'quotes', 'sales'].map(k => {
          const v = bd[k];
          const labels = { calls: 'Lla', visits: 'Vis', quotes: 'Cot', sales: 'Vta' };
          return (
            <div key={k} className="text-[8px] text-center">
              <p className="text-[var(--text-faint)] font-bold uppercase">{labels[k]}</p>
              <p className={`font-black ${v === null || v === undefined ? 'text-[var(--text-faint)]' : v >= 100 ? 'text-emerald-500' : v >= 70 ? 'text-blue-500' : v >= 40 ? 'text-amber-500' : 'text-red-500'}`}>
                {v === null || v === undefined ? '—' : `${v}%`}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const METAS_RAPIDAS = [
  { k: 'daily_calls', emoji: '📞', label: 'Llamadas/día' },
  { k: 'daily_visits', emoji: '📍', label: 'Visitas/día' },
  { k: 'daily_quotes', emoji: '📄', label: 'Cotizaciones/día' },
];

const MetasDiariasRapidas = ({ vendor }) => {
  const [metas, setMetas] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!vendor?.id) return;
    const now = new Date();
    setMetas(null);
    api.get(`/work-plan/goals?user_id=${vendor.id}&year=${now.getFullYear()}&month=${now.getMonth() + 1}`)
      .then((r) => {
        const g = (Array.isArray(r.data) ? r.data[0] : r.data) || {};
        setMetas({ daily_calls: g.daily_calls ?? 0, daily_visits: g.daily_visits ?? 0, daily_quotes: g.daily_quotes ?? 0 });
      })
      .catch(() => setMetas({ daily_calls: 0, daily_visits: 0, daily_quotes: 0 }));
  }, [vendor?.id]);

  const ajustar = (k, delta) => setMetas((m) => ({ ...m, [k]: Math.max(0, (m[k] || 0) + delta) }));

  const guardar = async () => {
    setSaving(true);
    try {
      await api.patch('/work-plan/goals/metas-diarias', { user_id: vendor.id, ...metas });
      toast.success(`Metas diarias de ${vendor.name} guardadas — las verá en su Mi Día`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron guardar las metas');
    } finally {
      setSaving(false);
    }
  };

  if (!metas) return <div className="border border-[var(--border)] rounded-[12px] p-3 text-xs text-[var(--text-muted)]">Cargando metas…</div>;

  return (
    <div className="border-2 border-blue-500/30 bg-blue-500/5 rounded-[14px] p-3" data-testid="metas-diarias-rapidas">
      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-500 mb-2">⚡ Metas diarias de este mes</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {METAS_RAPIDAS.map(({ k, emoji, label }) => (
          <div key={k} className="bg-[var(--surface)] border border-[var(--border)] rounded-[10px] p-2 text-center">
            <p className="text-[10px] font-black text-[var(--text-muted)]">{emoji} {label}</p>
            <div className="flex items-center justify-center gap-2 mt-1">
              <button type="button" onClick={() => ajustar(k, -1)}
                className="w-9 h-9 rounded-lg border border-[var(--border)] text-lg font-black text-[var(--text)] active:scale-95"
                data-testid={`meta-menos-${k}`}>−</button>
              <span className="text-xl font-black w-8 text-[var(--text)]" data-testid={`meta-valor-${k}`}>{metas[k]}</span>
              <button type="button" onClick={() => ajustar(k, 1)}
                className="w-9 h-9 rounded-lg border border-blue-500/50 bg-blue-500/10 text-lg font-black text-blue-500 active:scale-95"
                data-testid={`meta-mas-${k}`}>＋</button>
            </div>
          </div>
        ))}
      </div>
      <Button onClick={guardar} disabled={saving} className="w-full mt-2 h-10 bg-blue-600 hover:bg-blue-700 text-white font-black" data-testid="metas-diarias-guardar">
        {saving ? 'Guardando…' : 'Guardar metas diarias'}
      </Button>
    </div>
  );
};

const Field = ({ label, icon: Icon, children }) => (
  <div>
    <label className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)] mb-1">
      {Icon && <Icon size={12} weight="bold" />}
      {label}
    </label>
    {children}
  </div>
);

const MetricCard = ({ icon: Icon, label, value, color }) => {
  const colors = {
    emerald: 'text-emerald-400 border-emerald-500/20 bg-emerald-500/5',
    blue: 'text-[#1D4ED8] border-[#1D4ED8]/20 bg-[#1D4ED8]/5',
    yellow: 'text-yellow-400 border-yellow-500/20 bg-yellow-500/5',
    slate: 'text-[var(--text)] border-[var(--border)] bg-[var(--surface-muted)]',
  };
  return (
    <div className={`border rounded-[12px] p-4 ${colors[color]}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={14} weight="bold" />
        <span className="text-[10px] uppercase tracking-wide font-bold opacity-80">{label}</span>
      </div>
      <p className="text-xl font-black">{value}</p>
    </div>
  );
};

const PlatformRow = ({ icon: Icon, color, label, assigned, answered }) => {
  const rate = assigned > 0 ? Math.round((answered / assigned) * 100) : 0;
  return (
    <div
      className="border border-[var(--border)] bg-white/[0.02] rounded-[12px] p-3 flex items-center gap-3"
      data-testid={`platform-row-${label.toLowerCase()}`}
    >
      <div
        className="w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${color}1A`, color }}
      >
        <Icon size={20} weight="fill" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-bold text-[var(--text)]">{label}</p>
        <p className="text-[10px] text-[var(--text-muted)]">
          {answered}/{assigned} contestadas · {rate}%
        </p>
      </div>
      <div className="text-right shrink-0">
        <div className="flex gap-3">
          <div>
            <p className="text-[9px] uppercase tracking-wide text-[var(--text-faint)] font-bold">Asign.</p>
            <p className="text-sm font-black text-[var(--text)]" data-testid={`assigned-${label.toLowerCase()}`}>{assigned}</p>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-wide text-[var(--text-faint)] font-bold">Cont.</p>
            <p className="text-sm font-black text-emerald-400" data-testid={`answered-${label.toLowerCase()}`}>{answered}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Vendors;
